import { AfterViewInit, Component, EventEmitter, Input, OnDestroy, OnInit, Output, Renderer2, ViewChild } from '@angular/core';
import * as _ from 'lodash';
import { LazyLoadEvent, SortEvent } from 'primeng/api';
import { Table } from 'primeng/table';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { SelectedReportDetailsService } from '../../reports/report-generator/selected-report-details.service';
import { CreateNumericLabelPipe } from '../../shared/pipes/create-numeric-label.pipe';
import { DateTimeFormatterPipe } from '../../shared/pipes/date-time-formatter.pipe';
import { CreateThicknessLabelPipe } from '../../shared/pipes/thickness-management/create-thickness-label.pipe';
import { compareStrings, getPropertyValueByPath } from '../../utils';
import { ColumnInputType } from './column-input-type';
import { ColumnOutputType } from './column-output-type';
import { CustomTableFilterMatchers } from './filtering/custom-table-filter-matchers';
import { CustomTableFiltersService } from './filtering/custom-table-filters.service';
import { DateTimeFormatterLabelPipe } from './pipes/date-time-formatter-label.pipe';
import { TableColumn } from './table-column';
import { TableConfig } from './table-config';
import { TableEditMode } from './table-edit-mode';
import { TableOverridesService } from './table-overrides.service';
import { TableRowEditMode } from './table-row-edit-mode';
import { TableSelectionMode } from './table-selection-mode';

@Component({
  selector: 'hb-table',
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.scss'],
})
export class TableComponent implements AfterViewInit, OnInit, OnDestroy {
  @ViewChild('table', { static: false }) public tableRef: Table;

  public inputType = ColumnInputType;
  public outputType = ColumnOutputType;
  public editMode = TableEditMode;
  public rowEditMode = TableRowEditMode;

  @Input() public config: TableConfig;
  @Input() public data: any[] = [];
  @Input() public selectedItems: any;
  @Input() public resetSort: Observable<boolean>;
  @Input() public resetSelectedAll: Observable<boolean>;

  @Output() public addClicked: EventEmitter<any> = new EventEmitter<any>();
  @Output() public deleteClicked: EventEmitter<any> = new EventEmitter<any>();
  @Output() public editClicked: EventEmitter<any> = new EventEmitter<any>();
  @Output() public editConfirmClicked: EventEmitter<any> = new EventEmitter<any>();
  @Output() public editCancelClicked: EventEmitter<any> = new EventEmitter<any>();
  @Output() public loadLazy: EventEmitter<LazyLoadEvent> = new EventEmitter<LazyLoadEvent>();
  @Output() public onRowSelect: EventEmitter<any> = new EventEmitter<any>();
  @Output() public onRowUnselect: EventEmitter<any> = new EventEmitter<any>();
  @Output() public selectionChange: EventEmitter<any> = new EventEmitter<any>();
  @Output() public onRowReorder: EventEmitter<any> = new EventEmitter<any>();
  @Output() public selectAllChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() public selectAllOnPageChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() public dataChange: EventEmitter<any> = new EventEmitter<any>();
  @Output() public onTableAction: EventEmitter<any> = new EventEmitter<any>();
  @Output() public valueChanged: EventEmitter<any> = new EventEmitter<any>(); // editable cell value edited

  public selectedAll: boolean = false;
  public tableSelectionMode = TableSelectionMode;

  private multiSelectClickListener: any;
  private unsubscribe: Subject<boolean> = new Subject();

  constructor(
    private tableOverridesService: TableOverridesService,
    private customTableFilterService: CustomTableFiltersService,
    private renderer: Renderer2,
    private selectedReportDetailsService: SelectedReportDetailsService,
  ) { }

  public ngOnDestroy(): void {
    if (this.multiSelectClickListener) {
      this.multiSelectClickListener();
    }

    this.unsubscribe.next(true);
    this.unsubscribe.complete();
  }

  public ngOnInit(): void {
    this.tableOverridesService.overridesForMultipleSelectionMode();
    this.setupMultiSelect();
    this.subsribeToResetSort();
    this.subscribeToResetSelectedAll();

  }

  private subsribeToResetSort(): void {
    if (!this.resetSort) { return; }

    this.resetSort.pipe(takeUntil(this.unsubscribe)).subscribe(this.resetSortSettings.bind(this));
  }

  private subscribeToResetSelectedAll(): void {
    if (!this.resetSelectedAll) {
      return;
    }

    this.resetSelectedAll
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((resetRequested: boolean) => {
        if (resetRequested) {
          this.changeSelectedAll(false);
        }
      });
  }

  public ngAfterViewInit() {
    this.setPaginatorControlIds();

    // Filter must be added/modified AFTER init
    this.customTableFilterService.addCustomFilters(this.tableRef);
  }

  public rowNgClass(rowData: any): string {
    let styleClass: string;

    if (this.config.rowHighlightFunction) {
      styleClass = this.config.rowHighlightFunction(rowData);
    }

    styleClass += this.config.preventRowUnselect ? ' prevent-row-unselect' : '';

    return styleClass;
  }

  public modelChanged(rowData: any, col: TableColumn, $event: any): void {
    this.valueChanged.emit({ rowData, column: col, event: $event });
    _.set(rowData, col.valueField, $event);

    if (col.modelChangeEvent) {
      col.modelChangeEvent(rowData);
    }
  }

  public editRow(rowData: any): void {
    this.editClicked.emit(rowData);
  }

  public editConfirm(rowData: any): void {
    this.editConfirmClicked.emit(rowData);
  }

  public editCancel(rowData: any): void {
    this.editCancelClicked.emit(rowData);
  }

  public toggleFilters(): void {
    this.config.showFilters = !this.config.showFilters;

    if (!this.config.showFilters) {
      this.config.columns.forEach(col => {
        const columnName = col.inputType === this.inputType.Dropdown ? col.labelField : col.valueField;

        this.tableRef.filter('', columnName, 'gt');
      });
    }
  }

  public onPage(pageData: any): void {
    this.config.rowsPerPage = pageData.rows;
  }

  private setPaginatorControlIds(): void {
    if (!this.tableRef) {
      return;
    }

    const pageFirst = this.tableRef.el.nativeElement.querySelector('.ui-paginator-first');
    const pagePrev = this.tableRef.el.nativeElement.querySelector('.ui-paginator-prev');
    const pageNext = this.tableRef.el.nativeElement.querySelector('.ui-paginator-next');
    const pageLast = this.tableRef.el.nativeElement.querySelector('.ui-paginator-last');

    if (pageFirst) {
      pageFirst.setAttribute('id', this.config.id + '-paginator-first');
    }

    if (pagePrev) {
      pagePrev.setAttribute('id', this.config.id + '-paginator-prev');
    }

    if (pageNext) {
      pageNext.setAttribute('id', this.config.id + '-paginator-next');
    }

    if (pageLast) {
      pageLast.setAttribute('id', this.config.id + '-paginator-last');
    }
  }

  private setupMultiSelect(): void {
    if (this.config && this.config.selectionMode === TableSelectionMode.Multiple) {
      if (this.config.selectAllOnInit) {
        this.changeSelectedAll(true);
      }
    }
  }

  public toggleSelectAll(): void {
    this.selectedAll = !this.selectedAll;

    if (this.selectedAll) {
      this.selectedItems = [...this.data];
    } else {
      this.selectedItems = [];
    }
    this.selectionChange.emit(this.selectedItems);

    this.selectAllChange.emit(this.selectedAll);
  }

  public selectionChanged($event): void {
    if (this.config.selectionMode === TableSelectionMode.Multiple) {
      if ((this.selectedItems.length === this.data.length && !this.config.lazyLoading) ||
        this.selectedItems.length === this.config.totalRecords) {
        this.changeSelectedAll(true);
      } else {
        // must reset selection of all if now not all items are selected
        this.changeSelectedAll(false);
      }

      if (this.config.lazyLoading && this.config.dataKey) {
        const currentPageSelectionCount = this.data.filter(x =>
          this.selectedItems.find(t => t[this.config.dataKey] === x[this.config.dataKey])).length;

        if (currentPageSelectionCount === this.data.length) {
          this.selectAllOnPageChange.emit(true);
        }

        if (currentPageSelectionCount === 0) {
          this.selectAllOnPageChange.emit(false);
        }
      }
    }

    this.selectionChange.emit($event);
  }

  private changeSelectedAll(newValue: boolean): void {
    this.selectedAll = newValue;
    this.selectAllChange.emit(newValue);
  }

  public clearSelection(){
    this.selectionChange.emit([]);
    this.selectedItems=[]

    if(this.selectedAll){
      this.changeSelectedAll(false);
    }
  }

  public setupHeaderFilters(filterValue: any, column: TableColumn): void {
    const fieldParam = column.inputType === this.inputType.Dropdown ? column.labelField : column.valueField;
    let matchMode = 'contains';

    if (
      column.formatterPipe &&
      (column.formatterPipe.name === DateTimeFormatterLabelPipe.name || column.formatterPipe.name === DateTimeFormatterPipe.name)
    ) {
      matchMode = CustomTableFilterMatchers.FormattedDateMatcher;
    }

    if (
      column.formatterPipe &&
      (column.formatterPipe.name === CreateNumericLabelPipe.name || column.formatterPipe.name === CreateThicknessLabelPipe.name)
    ) {
      matchMode = CustomTableFilterMatchers.FormattedNumericMatcher;
    }

    this.tableRef.filter(filterValue, fieldParam, matchMode);
  }

  // calling if only config.useSortTransformation = true
  public customSort(event: SortEvent): void {
    const column = this.config.columns.find(c => c.valueField === event.field);

    event.data.sort((data1, data2) => {
      const data1fieldValue = getPropertyValueByPath(data1, event.field);
      const data2fieldValue = getPropertyValueByPath(data2, event.field);
      const value1 = column.sortTransformation(data1fieldValue);
      const value2 = column.sortTransformation(data2fieldValue);
      const result = compareStrings(value1, value2);
      return result * event.order;
    });
  }

  public dataChanged(rowData: any): void {
    this.dataChange.emit(rowData);
  }

  public onChangeMultiSelect($event: any, col: any, rowIndex: any, rowData: any): void {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (!col.dropdownChangedEvent) { return; }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    col.dropdownChangedEvent($event, rowIndex, rowData);
  }

  public onMultiSelectPanelShow(): void {
    if (!this.tableRef) {
      return;
    }

    const panel = document.getElementsByClassName('hb-multiselect-panel');

    if (panel.length === 0) {
      return;
    }

    // This is workaround to prevent closing multiselect panel when user clicks on it (there is no more suitable solution with current version of primeng)
    this.multiSelectClickListener = this.renderer.listen(panel[0], 'click', this.onClickMultiSelectPanel.bind(this));
  }

  public onClickMultiSelectPanel(event: any): void {
    event.stopPropagation();
  }

  public onMultiSelectPanelHide(): void {
    if (this.multiSelectClickListener) {
      this.multiSelectClickListener();
    }
  }

  private resetSortSettings(): void {
    if (!this.config) { return; }

    this.tableRef.sortOrder = this.config.startingSortOrder;
    this.tableRef.sortField = this.config.startingSortField;
  }
}
